import React from 'react'

import { MEETING_FORMAT_CHOICE } from '@/components/integration/nylas/scheduler/constants'
import { useSchedulerConfig } from '@/components/integration/nylas/scheduler/context/SchedulerConfigContext'
import { useSchedulerSelection } from '@/components/integration/nylas/scheduler/context/SchedulerSelectionContext'
import { getOfferedInPersonMeetingFormats } from '@/components/integration/nylas/scheduler/meetingFormatSelection'
import { InPersonMeetingFormat } from '@/components/integration/nylas/scheduler/types'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/lib/utils'

interface Props {
  isBookingAsClient?: boolean
}

/**
 * "Where?" - at the adviser's office or at the client's home. Only asked when
 * the meeting type offers both; with one in-person format, "In person" already
 * says where.
 */
const InPersonPlacePicker: React.FC<Props> = ({ isBookingAsClient = true }) => {
  const { availableMeetingFormats = [] } = useSchedulerConfig()
  const {
    selectedFormatChoice,
    selectedInPersonFormat,
    setSelectedInPersonFormat,
  } = useSchedulerSelection()

  const offeredInPersonFormats = getOfferedInPersonMeetingFormats(
    availableMeetingFormats,
  )

  const shouldShow =
    selectedFormatChoice === MEETING_FORMAT_CHOICE.in_person &&
    offeredInPersonFormats.length > 1

  if (!shouldShow) {
    return null
  }

  const IN_PERSON_OPTIONS: Array<{
    inPersonFormat: InPersonMeetingFormat
    label: string
  }> = [
    {
      inPersonFormat: 'in_person_office',
      label: "At the adviser's office",
    },
    {
      inPersonFormat: 'in_person_client_home',
      label: isBookingAsClient ? 'At your home' : "At the client's home",
    },
  ]

  function handleValueChange(value: string) {
    const chosenInPersonFormat = offeredInPersonFormats.find(
      (inPersonFormat) => inPersonFormat === value,
    )

    if (chosenInPersonFormat) {
      setSelectedInPersonFormat(chosenInPersonFormat)
    }
  }

  return (
    <div className="flex flex-col gap-3 mb-6">
      <span id="scheduler-in-person-place-label">Where?</span>

      <RadioGroup
        aria-labelledby="scheduler-in-person-place-label"
        value={selectedInPersonFormat ?? ''}
        onValueChange={handleValueChange}
        className="grid gap-2 @md:grid-cols-2"
      >
        {IN_PERSON_OPTIONS.map(({ inPersonFormat, label }) => {
          const isSelected = selectedInPersonFormat === inPersonFormat

          return (
            <label
              key={inPersonFormat}
              className={cn(
                `flex cursor-pointer items-center gap-3 rounded-lg
                border-[1.5px] bg-white px-4 py-3 text-sm`,
                isSelected
                  ? 'border-primary font-semibold'
                  : 'border-gray-300 text-foreground-dark',
              )}
            >
              <RadioGroupItem
                value={inPersonFormat}
                id={`scheduler-in-person-place-${inPersonFormat}`}
              />
              {label}
            </label>
          )
        })}
      </RadioGroup>
    </div>
  )
}

export default InPersonPlacePicker
